import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { HardHat, Plus, Camera, ShieldCheck, Lock, CheckCircle2, Circle } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { Spinner } from "@/components/tac";

const TRADES = ["electrical", "plumbing", "hvac", "low_voltage"];
const CHECKLIST = [
  "Rough-in matches approved plan",
  "No exposed or unprotected conductors",
  "Firestopping and penetrations sealed",
  "Boxes secured and accessible",
  "Labeling and identification complete",
];
const inp = "w-full rounded-lg border border-slate-700 bg-[#0d1420] px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none";

function fileToB64(file) {
  return new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result.split(",")[1]); r.onerror = rej; r.readAsDataURL(file); });
}
function getGeo() {
  return new Promise((res) => { if (!navigator.geolocation) return res({ lat: null, lng: null });
    navigator.geolocation.getCurrentPosition((p) => res({ lat: p.coords.latitude, lng: p.coords.longitude }), () => res({ lat: null, lng: null }), { timeout: 4000 }); });
}

function Zone({ z, canEdit, onChange }) {
  const [insp, setInsp] = useState({ inspector: "", passcode: "", checklist: CHECKLIST.map((i) => ({ item: i, ok: false })) });
  const [busy, setBusy] = useState(false);
  const toggle = async (trade, done) => { try { await api.patch(`/zones/${z.id}/roughin`, { trade, done }); onChange(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };
  const photo = async (e) => {
    const f = e.target.files?.[0]; if (!f) return; setBusy(true);
    try { const geo = await getGeo(); const data = await fileToB64(f);
      const r = await api.post(`/zones/${z.id}/photo`, { filename: f.name, data, content_type: f.type || "image/jpeg", lat: geo.lat, lng: geo.lng });
      toast.success(`Photo captured${r.data.geo_tagged ? " (geo-tagged)" : ""}`); onChange();
    } catch (er) { toast.error(apiErr(er.response?.data?.detail)); } setBusy(false); e.target.value = "";
  };
  const ready = async () => { try { await api.post(`/zones/${z.id}/ready`); toast.success("Zone ready for inspection"); onChange(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };
  const signOff = async () => {
    setBusy(true);
    try { const r = await api.post(`/zones/${z.id}/inspection`, insp);
      toast[r.data.trim_unlocked ? "success" : "error"](r.data.trim_unlocked ? "Passed - trim-out unlocked" : "Inspection failed"); onChange();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } setBusy(false);
  };

  return (
    <div className="card-tac p-5" data-testid={`zone-${z.id}`}>
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-display text-lg font-semibold text-white">{z.name}</h3>
        {z.trim_unlocked ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-1 text-[10px] font-semibold text-emerald-300" data-testid={`zone-unlocked-${z.id}`}><ShieldCheck className="h-3 w-3" /> Trim-out unlocked</span>
          : <span className="inline-flex items-center gap-1 rounded-full bg-slate-700/40 px-2 py-1 text-[10px] text-slate-400"><Lock className="h-3 w-3" /> Trim locked</span>}
      </div>
      {/* Rough-in tasks */}
      <div className="mb-4 flex flex-wrap gap-2">
        {(z.tasks || []).map((t) => (
          <button key={t.trade} disabled={!canEdit} onClick={() => toggle(t.trade, !t.done)} data-testid={`roughin-${z.id}-${t.trade}`}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs ${t.done ? "bg-emerald-500/15 text-emerald-300" : "border border-slate-700 text-slate-400"}`}>
            {t.done ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Circle className="h-3.5 w-3.5" />} {t.trade}
          </button>
        ))}
        {!(z.tasks || []).length && <span className="text-xs text-slate-600">No trades assigned.</span>}
      </div>
      {/* Before-drywall photo gate */}
      <div className="mb-4 rounded-xl border border-slate-800 p-3">
        <div className="mb-2 flex items-center justify-between">
          <span className="font-mono-tac text-[11px] uppercase tracking-widest text-slate-500">Before-drywall evidence</span>
          <span className="font-mono-tac text-[11px] text-slate-400">{(z.photos || []).length} photo(s)</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-slate-700 px-3 py-1.5 text-xs text-white hover:border-amber-500" data-testid={`zone-photo-label-${z.id}`}>
            <Camera className="h-3.5 w-3.5" /> {busy ? "Uploading…" : "Capture photo"}
            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={photo} data-testid={`zone-photo-input-${z.id}`} />
          </label>
          {(z.photos || []).some((p) => p.geo_tagged) && <span className="font-mono-tac text-[10px] text-emerald-400">geo-tagged ✓</span>}
          {!z.drywall_ready && canEdit && <button onClick={ready} data-testid={`zone-ready-${z.id}`} className="rounded-full bg-cyan-500/15 px-3 py-1.5 text-xs text-cyan-300 hover:bg-cyan-500/25">Mark ready for inspection</button>}
          {z.drywall_ready && <span className="font-mono-tac text-[10px] text-amber-400">ready for inspection</span>}
        </div>
      </div>
      {/* Inspection sign-off */}
      {z.inspection ? (
        <div className={`text-xs ${z.inspection.status === "passed" ? "text-emerald-400" : "text-rose-400"}`} data-testid={`zone-inspection-${z.id}`}>
          Inspection {z.inspection.status} by {z.inspection.inspector} · ref {z.inspection.passcode_ref}
        </div>
      ) : z.drywall_ready && canEdit ? (
        <div className="rounded-xl border border-slate-800 p-3" data-testid={`inspection-form-${z.id}`}>
          <div className="mb-2 font-mono-tac text-[11px] uppercase tracking-widest text-slate-500">Inspector sign-off</div>
          {insp.checklist.map((c, i) => (
            <label key={i} className="flex items-center gap-2 py-1 text-xs text-slate-300">
              <input type="checkbox" checked={c.ok} onChange={() => setInsp({ ...insp, checklist: insp.checklist.map((x, j) => (j === i ? { ...x, ok: !x.ok } : x)) })} className="accent-amber-500" data-testid={`insp-item-${z.id}-${i}`} />
              {c.item}
            </label>
          ))}
          <div className="mt-2 grid grid-cols-2 gap-2">
            <input placeholder="Inspector name" value={insp.inspector} onChange={(e) => setInsp({ ...insp, inspector: e.target.value })} className={inp} data-testid={`insp-inspector-${z.id}`} />
            <input placeholder="Sign-off passcode" value={insp.passcode} onChange={(e) => setInsp({ ...insp, passcode: e.target.value })} className={inp} data-testid={`insp-passcode-${z.id}`} />
          </div>
          <button disabled={busy} onClick={signOff} data-testid={`insp-submit-${z.id}`} className="btn-amber mt-3 rounded-full px-4 py-2 text-xs disabled:opacity-50">Submit inspection</button>
        </div>
      ) : null}
    </div>
  );
}

export default function ProjectConstruction({ projectId, canEdit }) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [trades, setTrades] = useState([]);
  const { data: zones, isLoading } = useQuery({ queryKey: ["zones", projectId], queryFn: async () => (await api.get(`/projects/${projectId}/zones`)).data });
  const refresh = () => qc.invalidateQueries({ queryKey: ["zones", projectId] });
  const create = async () => {
    try { await api.post(`/projects/${projectId}/zones`, { name, trades }); toast.success("Zone added"); setName(""); setTrades([]); refresh(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  if (isLoading) return <div className="mt-8"><Spinner label="Loading zones" /></div>;
  return (
    <div className="mt-8" data-testid="construction-tab">
      <h2 className="mb-3 flex items-center gap-2 font-mono-tac text-xs uppercase tracking-widest text-slate-500"><HardHat className="h-4 w-4 text-amber-400" /> Rough-in zones</h2>
      {canEdit && (
        <div className="card-tac mb-4 flex flex-wrap items-center gap-2 p-3">
          <input placeholder="Zone / room name" value={name} onChange={(e) => setName(e.target.value)} className={`${inp} max-w-xs`} data-testid="zone-name-input" />
          {TRADES.map((t) => (
            <button key={t} onClick={() => setTrades((tr) => tr.includes(t) ? tr.filter((x) => x !== t) : [...tr, t])}
              className={`rounded-full px-3 py-1.5 text-xs ${trades.includes(t) ? "bg-amber-500/20 text-amber-300 border border-amber-500/40" : "border border-slate-700 text-slate-400"}`} data-testid={`zone-trade-${t}`}>{t}</button>
          ))}
          <button disabled={!name.trim()} onClick={create} className="btn-amber inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs disabled:opacity-50" data-testid="add-zone-btn"><Plus className="h-3.5 w-3.5" /> Add zone</button>
        </div>
      )}
      <div className="grid gap-4 lg:grid-cols-2">
        {(zones || []).map((z) => <Zone key={z.id} z={z} canEdit={canEdit} onChange={refresh} />)}
        {!(zones || []).length && <div className="card-tac col-span-full py-12 text-center text-sm text-slate-500" data-testid="zones-empty">No zones yet. Add rooms/zones to track rough-in.</div>}
      </div>
    </div>
  );
}
